import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CompleteProfileSetupResponse } from '@ekhodealer/ekho-common';

import { apiClient } from '@config/server/apiClient';
import { queries } from '@config/server/queryKeys';

/**
 * Input type for completing profile setup (sent to backend)
 * Note: Password is updated client-side via Firebase Auth, not sent to backend
 */
export interface CompleteProfileSetupInput {
	email: string;
	firstName: string;
	lastName: string;
}

/**
 * API function to complete profile setup
 */
export const completeProfileSetup = async (
	input: CompleteProfileSetupInput,
): Promise<CompleteProfileSetupResponse> => {
	const response = await apiClient.post<CompleteProfileSetupResponse>(
		`/admin_portal/setup/profile`,
		input,
	);
	return response.data;
};

/**
 * Hook for completing profile setup (email, first name, last name, password)
 * Invalidates /me query on success to trigger guard-based routing
 */
export const useCompleteProfileSetup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CompleteProfileSetupInput) => {
			return completeProfileSetup(input);
		},
		onSuccess: () => {
			// Invalidate /me query to refresh user data with updated onboarding state
			// The InviteAuthGuard will automatically route based on the new onboarding state
			queryClient.invalidateQueries({
				queryKey: queries.auth.me.queryKey,
			});
		},
	});
};
